import notification from "antd/es/notification";
import { getLocation, push, RouterLocation } from "connected-react-router";
import { isEqual, omit } from "lodash-es";
import { defineMessage } from "react-intl";
import { all, call, delay, put, race, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 } from "uuid";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { EntityType } from "@mapmycustomers/shared/types/entity";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Person from "@mapmycustomers/shared/types/entity/Person";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel, {
  PinLegendsFilter,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isApiError, isDefined } from "@mapmycustomers/shared/util/assert";

import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import getFileRemovedNotificationNode from "@app/component/preview/util/getFileRemovedNotificationNode";
import getYourDownloadWillStartShortlyNode from "@app/component/preview/util/getYourDownloadWillStartShortlyNode";
import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import activitySettings from "@app/scene/activity/utils/activitySettings";
import { notifyActivityRelatedEntities } from "@app/store/activity/actions";
import { FieldValidationErrors } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import getNextAssociationsState, {
  NextAssociationsState,
} from "@app/store/associations/getNextAssociationsState";
import { getDealLockGetter } from "@app/store/deal";
import { getEntityTypesWithExternalPreview, getEntityViewSource } from "@app/store/entityView";
import { showEntityView } from "@app/store/entityView/actions";
import { EntityViewSource } from "@app/store/entityView/EntityViewPayload";
import { handleError } from "@app/store/errors/actions";
import {
  doesOrganizationSupportMultiContactActivities,
  getOrganizationId,
  shouldIncludeSharedActivities,
} from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import getCheckInRecordTypeErrorMap from "@app/util/activity/checkInRecordTypeMessages";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";
import isBulkEmailParentActivity from "@app/util/email/isBulkEmailParentActivity";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";
import { downloadFileByUrl } from "@app/util/file";
import { activityLayoutModel } from "@app/util/layout/impl";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";

import {
  addActivityCompany,
  addActivityDeal,
  bulkUpdateFiles,
  changeAssociatedEntities,
  deleteActivity,
  deleteActivityFile,
  downloadActivityFile,
  fetchChildActivities,
  fetchPreviewData,
  fetchThumbnail,
  postponeActivity,
  relateUnrelateEntities,
  removeActivityCompany,
  removeActivityDeal,
  setActivityPeople,
  toggleCompleteActivity,
  updateActivity,
  uploadActivityFiles,
} from "./actions";
import ActivityRecordData from "./ActivityRecordData";
import { getActivity, getRecordData } from "./selectors";

const processAssociatedEntity = <T extends Company | Deal | Person>(
  originalEntity: T | undefined,
  entityResult: SettleResult<T | undefined>
): T | undefined => {
  if (!entityResult.error) {
    return entityResult.result;
  } else if (originalEntity) {
    return {
      ...originalEntity,
      accessStatus: {
        ...(originalEntity.accessStatus ?? {}),
        delete: false,
        read: false,
        update: false,
      },
    };
  }
  return originalEntity;
};

function* getFetchActivityParams(): Generator<unknown, PlatformFilterModel, never> {
  const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);

  return {
    includeAccessStatus: true,
    includeCustomFields: true,
    includeEmailStats: true,
    includeFiles: true,
    includeUsersWithAccess: true,
    ...(includeSharedActivities
      ? {
          includeAccountSharedActivities: true,
          includeContactSharedActivities: true,
          includeDealSharedActivities: true,
        }
      : {}),
  };
}

function* onFetchPreviewData({ payload: activityId }: ReturnType<typeof fetchPreviewData.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const showSharedActivities = activitySettings.getSharedActivitiesVisibility();

    const activity: Activity = yield callApi(
      "fetchActivity",
      orgId,
      activityId,
      yield getFetchActivityParams()
    );

    const filesResponse: ListResponse<RawFile> = yield callApi(
      "fetchEntityFiles",
      orgId,
      EntityType.ACTIVITY,
      activity.id
    );

    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      company: activity.account,
      deal: activity.deal,
      entityType: EntityType.ACTIVITY,
      person: activity.contact,
    });

    const companyPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [
      EntityType.COMPANY,
    ]);
    const dealPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);
    const personPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const [companyResult, dealResult, personResult, childActivitiesResult]: [
      SettleResult<Company | undefined>,
      SettleResult<Deal | undefined>,
      SettleResult<Person | undefined>,
      SettleResult<ListResponse<Activity>>,
    ] = yield allSettled([
      activity.account?.id
        ? callApi("fetchCompany", orgId, activity.account.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            includeUsersWithAccess: true,
            pinLegends: companyPinLegends,
          })
        : undefined,
      activity.deal?.id
        ? callApi("fetchDeal", orgId, activity.deal.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            includeUsersWithAccess: true,
            pinLegends: dealPinLegends,
          })
        : undefined,
      activity.contact?.id
        ? callApi("fetchPerson", orgId, activity.contact.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            includeUsersWithAccess: true,
            pinLegends: personPinLegends,
          })
        : undefined,
      callApi("fetchActivities", orgId, {
        $filters: {
          $and: [{ parentId: { $eq: activity.id } }],
          includeEmails: true,
          includeFiles: true,
          ...(showSharedActivities
            ? {
                includeAccountSharedActivities: true,
                includeContactSharedActivities: true,
                includeDealSharedActivities: true,
              }
            : {}),
        },
        // Bulk emails list every recipient, so load all children at once.
        $limit: isBulkEmailParentActivity(activity) ? -1 : CHILD_ACTIVITIES_PAGE_SIZE,
        $offset: 0,
        $order: "-createdAt",
      }),
    ]);

    const associatedCompany = processAssociatedEntity(activity.account, companyResult);
    const associatedDeal = processAssociatedEntity(activity.deal, dealResult);
    const associatedPerson = processAssociatedEntity(activity.contact, personResult);

    const isMultiContactEnabled: boolean = yield select(
      doesOrganizationSupportMultiContactActivities
    );
    const peopleIds = (activity.contacts ?? []).map(({ id }) => id);
    let associatedPeople: Person[];
    if (isMultiContactEnabled && peopleIds.length) {
      let peopleById: Map<Person["id"], Person>;
      try {
        // Fetch with access status so the Relationships tab can show the per-person remove control.
        // Custom fields and groups are asked for because a legend can classify on either.
        const peopleResponse: ListResponse<Person> = yield callApi("fetchPeople", orgId, {
          $filters: {
            $and: [{ id: { $in: peopleIds } }],
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            pinLegends: personPinLegends,
          },
          $limit: peopleIds.length,
        });
        peopleById = new Map(peopleResponse.data.map((person) => [person.id, person]));
      } catch {
        // A people-fetch hiccup should only cost the per-person remove control, not the whole
        // pane, so fall back to the activity's own contacts (which lack access status).
        peopleById = new Map((activity.contacts ?? []).map((person) => [person.id, person]));
      }
      // Reuse the richly-fetched primary so associatedPeople[0] keeps the custom fields, groups,
      // territories, and users-with-access that the lighter fetchPeople call above omits.
      if (associatedPerson) {
        peopleById.set(associatedPerson.id, associatedPerson);
      }
      const primaryId = activity.contact?.id;
      const orderedPeopleIds = primaryId
        ? [primaryId, ...peopleIds.filter((personId) => personId !== primaryId)]
        : peopleIds;
      associatedPeople = orderedPeopleIds
        .map((personId) => peopleById.get(personId))
        .filter(isDefined);
    } else {
      associatedPeople = activity.contacts ?? (associatedPerson ? [associatedPerson] : []);
    }

    const childActivitiesResponse = childActivitiesResult.error
      ? { data: [], total: 0 }
      : childActivitiesResult.result;

    const recordData: ActivityRecordData = {
      activity,
      associations: {
        associatedCompany,
        associatedDeal,
        associatedPeople,
        associatedPerson,
        ...associationsState,
      },
      childActivities: childActivitiesResponse.data,
      childActivitiesTotal: childActivitiesResponse.total ?? childActivitiesResponse.data.length,
      files: filesResponse?.data ?? [],
    };

    yield put(fetchPreviewData.success({ recordData }));
  } catch (error) {
    const hasNoAccess = isApiError(error) && error.status === 404;
    yield put(fetchPreviewData.failure());
    if (!hasNoAccess) {
      yield put(handleError({ error }));
    }
  }
}

const toggleCompleteSuccessMessage = defineMessage({
  id: "component.activityRecordPane.saga.toggleComplete.success",
  defaultMessage: 'Activity{completed, select, true {} other { is no longer}} marked as "Done"',
  description: "Activity toggle complete success message",
});

const postponeSuccessMessage = defineMessage({
  id: "component.activityRecordPane.saga.postpone.success",
  defaultMessage: "Activity postponed successfully",
  description: "Activity postpone success message",
});

function* onToggleCompleteActivity({ payload }: ReturnType<typeof toggleCompleteActivity.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity;

    yield callApi("updateActivity", orgId, layoutId, {
      ...activity,
      completed: !activity.completed,
    });

    const updatedActivity: Activity = yield callApi(
      "fetchActivity",
      orgId,
      activity.id,
      yield getFetchActivityParams()
    );

    if (payload.callback) {
      yield call(payload.callback, updatedActivity);
    }
    yield put(toggleCompleteActivity.success(updatedActivity));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(
          toggleCompleteSuccessMessage,
          `Activity marked as "${updatedActivity.completed ? "Done" : "Not yet done"}"`,
          { completed: updatedActivity.completed }
        )
      ),
    });
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
    yield put(notifyActivityRelatedEntities(updatedActivity));
  } catch (error) {
    yield put(toggleCompleteActivity.failure());
    yield put(handleError({ error }));
  }
}

function* onPostponeActivity({ payload }: ReturnType<typeof postponeActivity.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity;

    yield callApi("updateActivity", orgId, layoutId, activity);

    const updatedActivity: Activity = yield callApi(
      "fetchActivity",
      orgId,
      activity.id,
      yield getFetchActivityParams()
    );

    if (payload.callback) {
      yield call(payload.callback, updatedActivity);
    }
    yield put(postponeActivity.success(updatedActivity));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(postponeSuccessMessage, "Activity postponed successfully")
      ),
    });
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
    yield put(notifyActivityRelatedEntities(updatedActivity));
  } catch (error) {
    yield put(postponeActivity.failure());
    yield put(handleError({ error }));
  }
}

const updateSuccessMessage = defineMessage({
  id: "component.activityRecordPane.saga.update.success",
  defaultMessage: "Activity updated successfully",
  description: "Activity update success message",
});

const REQUIRED_FIELD_ERROR_CODE = "v0586";

function* onUpdateActivity({ payload }: ReturnType<typeof updateActivity.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity, callback, customFields, fieldsValueValidateCallback, layoutId, updateType } =
      payload;

    const readOnlyFields: string[] = activityFieldModel.fields
      .filter((field) => !field.isEditable || field.hasFeature(FieldFeature.CALCULATED_FIELD))
      .map((field) => field.platformName);

    const strippedActivity = readOnlyFields.length
      ? (omit(activity, readOnlyFields) as Identified)
      : activity;

    const currentActivity: Activity | undefined = yield select(getActivity);
    const customFieldValues = customFields ? Object.values(customFields).filter(isDefined) : [];

    const changedCustomFields = customFieldValues.filter((customField) => {
      if (readOnlyFields.includes(customField.esKey)) {
        return false;
      }
      const oldCustomField = currentActivity?.customFields?.find(
        ({ customField: { id } }) => id === customField.customField.id
      );
      return !oldCustomField || !isEqual(oldCustomField.value, customField.value);
    });

    // Validate custom fields values
    if (changedCustomFields.length && fieldsValueValidateCallback) {
      let customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
        "validateCustomFieldsValues",
        orgId,
        layoutId,
        EntityType.ACTIVITY,
        activity.id,
        changedCustomFields
      );

      if (!activity.completed) {
        customFieldValidateResponse = customFieldValidateResponse
          .map((fieldError) => ({
            ...fieldError,
            error: fieldError.error.filter(({ code }) => code !== REQUIRED_FIELD_ERROR_CODE),
          }))
          .filter(({ error }) => error.length);
      }

      if (customFieldValidateResponse.length) {
        yield call(fieldsValueValidateCallback, customFieldValidateResponse);
        yield put(updateActivity.failure());
        return;
      }
    }

    yield callApi(
      "updateActivity",
      orgId,
      layoutId,
      strippedActivity,
      updateType ? { updateRecurring: updateType } : undefined
    );

    if (changedCustomFields.length) {
      yield callApi(
        "upsertCustomFieldsValues",
        orgId,
        layoutId,
        EntityType.ACTIVITY,
        activity.id,
        changedCustomFields
      );
    }

    const updatedActivity: Activity = yield callApi(
      "fetchActivity",
      orgId,
      activity.id,
      yield getFetchActivityParams()
    );

    if (callback) {
      yield call(callback, updatedActivity);
    }
    yield put(updateActivity.success(updatedActivity));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(updateSuccessMessage, "Activity updated successfully")
      ),
    });
    yield put(
      notifyAboutChanges({
        entityType: EntityType.ACTIVITY,
        possiblyOtherRecordsAffected: !!updateType,
        updated: [updatedActivity],
      })
    );
    yield put(notifyActivityRelatedEntities(updatedActivity));
  } catch (error) {
    yield put(updateActivity.failure());
    yield put(
      handleError({
        error,
        messageByErrorCodeMap: getCheckInRecordTypeErrorMap(
          payload.activity.checkInType as Activity["checkInType"]
        ),
      })
    );
  }
}

function* onAddActivityCompany({
  payload: { id, companyId },
}: ReturnType<typeof addActivityCompany.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity } = yield select(getRecordData);
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.COMPANY]);

    const [updatedActivity, company]: [Activity, Company] = yield all([
      callApi("updateActivity", orgId, layoutId, { id, account: { id: companyId } }),
      callApi("fetchCompany", orgId, companyId, {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends,
      }),
    ]);

    const { associations } = yield select(getRecordData);
    const nextAssociations: NextAssociationsState = yield call(getNextAssociationsState, {
      company,
      deal: associations.associatedDeal,
      entityType: EntityType.ACTIVITY,
      person: associations.associatedPerson,
    });

    yield put(addActivityCompany.success({ company, nextAssociations }));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(addActivityCompany.failure());
    yield put(handleError({ error }));
  }
}

function* onAddActivityDeal({
  payload: { id, dealId },
}: ReturnType<typeof addActivityDeal.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity } = yield select(getRecordData);
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);

    const [updatedActivity, deal]: [Activity, Deal] = yield all([
      callApi("updateActivity", orgId, layoutId, { id, deal: { id: dealId } }),
      callApi("fetchDeal", orgId, dealId, {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends,
      }),
    ]);

    const { associations } = yield select(getRecordData);
    const nextAssociations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: associations.associatedCompany,
      deal,
      entityType: EntityType.ACTIVITY,
      person: associations.associatedPerson,
    });

    yield put(addActivityDeal.success({ deal, nextAssociations }));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(addActivityDeal.failure());
    yield put(handleError({ error }));
  }
}

export function* onSetActivityPeople({
  payload: { id, peopleIds, primaryPersonId },
}: ReturnType<typeof setActivityPeople.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity, associations } = yield select(getRecordData);
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;
    const isMultiContactEnabled: boolean = yield select(
      doesOrganizationSupportMultiContactActivities
    );

    const orderedIds = primaryPersonId
      ? [primaryPersonId, ...peopleIds.filter((personId) => personId !== primaryPersonId)]
      : peopleIds;

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    // Fetch with access status so the Relationships tab can show the per-person remove control.
    // Custom fields and groups are asked for because a legend can classify on either.
    const peopleResponse: ListResponse<Person> = orderedIds.length
      ? yield callApi("fetchPeople", orgId, {
          $filters: {
            $and: [{ id: { $in: orderedIds } }],
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            pinLegends,
          },
          $limit: orderedIds.length,
        })
      : { data: [], total: 0 };
    const peopleById = new Map(peopleResponse.data.map((person) => [person.id, person]));

    const people: Person[] = orderedIds
      .map((personId) => peopleById.get(personId))
      .filter(isDefined);

    const updatePayload = isMultiContactEnabled
      ? {
          id,
          contact: orderedIds[0] ? { id: orderedIds[0] } : null,
          contacts: orderedIds.map((personId) => ({ id: personId })),
        }
      : {
          id,
          contact: orderedIds[0] ? { id: orderedIds[0] } : null,
        };

    const updatedActivity: Activity = yield callApi(
      "updateActivity",
      orgId,
      layoutId,
      updatePayload
    );

    const nextAssociations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: associations.associatedCompany,
      deal: associations.associatedDeal,
      entityType: EntityType.ACTIVITY,
      people,
      person: people[0],
    });

    yield put(setActivityPeople.success({ isMultiContactEnabled, nextAssociations, people }));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(setActivityPeople.failure());
    yield put(handleError({ error }));
  }
}

function* onRemoveActivityCompany({
  payload: activityId,
}: ReturnType<typeof removeActivityCompany.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity } = yield select(getRecordData);
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;

    const updatedActivity: Activity = yield callApi("updateActivity", orgId, layoutId, {
      id: activityId,
      account: null,
    });

    const { associations } = yield select(getRecordData);
    const nextAssociations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: undefined,
      deal: associations.associatedDeal,
      entityType: EntityType.ACTIVITY,
      person: associations.associatedPerson,
    });

    yield put(removeActivityCompany.success(nextAssociations));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(removeActivityCompany.failure());
    yield put(handleError({ error }));
  }
}

function* onRemoveActivityDeal({
  payload: activityId,
}: ReturnType<typeof removeActivityDeal.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { activity } = yield select(getRecordData);
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;

    const updatedActivity: Activity = yield callApi("updateActivity", orgId, layoutId, {
      id: activityId,
      deal: null,
    });

    const { associations } = yield select(getRecordData);
    const nextAssociations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: associations.associatedCompany,
      deal: undefined,
      entityType: EntityType.ACTIVITY,
      person: associations.associatedPerson,
    });

    yield put(removeActivityDeal.success(nextAssociations));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(removeActivityDeal.failure());
    yield put(handleError({ error }));
  }
}

function* onChangeAssociatedEntities({
  payload,
}: ReturnType<typeof changeAssociatedEntities.request>) {
  try {
    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      ...payload,
      entityType: EntityType.ACTIVITY,
    });
    yield put(changeAssociatedEntities.success(associationsState));
  } catch (error) {
    yield put(changeAssociatedEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onRelateUnrelateEntities({
  payload: {
    associatedCompany,
    associatedDeal,
    associatedPerson,
    isDealCorrectlyRelatedToCompany,
    isDealCorrectlyRelatedToPerson,
    isPersonCorrectlyRelatedToCompany,
    unrelate,
  },
}: ReturnType<typeof relateUnrelateEntities.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    let person: Person | undefined;
    let deal: Deal | undefined;

    // The prompt counts a deal the lock keeps from being edited as related, so a relate never
    // reaches one; an Undo must not unlink one either.
    const getDealLock: ReturnType<typeof getDealLockGetter> = yield select(getDealLockGetter);
    const canRelinkDeal = !!associatedDeal && getDealLock(associatedDeal).canEdit;

    let updatePerson = false,
      updateDeal = false,
      updateCompany = false;

    if (!isPersonCorrectlyRelatedToCompany && associatedPerson && associatedCompany) {
      updatePerson = true;
      updateCompany = true;

      person = yield callApi("updatePerson", orgId, undefined, {
        id: associatedPerson.id,
        accounts: [...associatedPerson.accounts, { id: associatedCompany.id }],
      });
    } else if (
      isPersonCorrectlyRelatedToCompany &&
      associatedPerson &&
      associatedCompany &&
      unrelate
    ) {
      updatePerson = true;
      updateCompany = true;

      person = yield callApi("updatePerson", orgId, undefined, {
        id: associatedPerson.id,
        accounts: associatedPerson.accounts.filter(({ id }) => id !== associatedCompany.id),
      });
    }

    if ((!isDealCorrectlyRelatedToCompany || !isDealCorrectlyRelatedToPerson) && associatedDeal) {
      updateDeal = true;
      updateCompany = !isDealCorrectlyRelatedToCompany && !!associatedCompany;
      updatePerson = !isDealCorrectlyRelatedToPerson && !!associatedPerson;

      deal = yield callApi("updateDeal", orgId, undefined, {
        id: associatedDeal.id,
        ...(!isDealCorrectlyRelatedToCompany && associatedCompany
          ? { account: { id: associatedCompany.id } }
          : {}),
        ...(!isDealCorrectlyRelatedToPerson && associatedPerson
          ? { contact: { id: associatedPerson.id } }
          : {}),
      });
    } else if (
      (isDealCorrectlyRelatedToCompany || isDealCorrectlyRelatedToPerson) &&
      associatedDeal &&
      canRelinkDeal &&
      unrelate
    ) {
      updateDeal = true;
      updateCompany = isDealCorrectlyRelatedToCompany && !!associatedCompany;
      updatePerson = isDealCorrectlyRelatedToPerson && !!associatedPerson;

      deal = yield callApi("updateDeal", orgId, undefined, {
        id: associatedDeal.id,
        ...(isDealCorrectlyRelatedToCompany ? { account: null } : {}),
        ...(isDealCorrectlyRelatedToPerson ? { contact: null } : {}),
      });
    }

    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      company: associatedCompany,
      deal: deal ?? associatedDeal,
      entityType: EntityType.ACTIVITY,
      person: person ?? associatedPerson,
    });

    yield put(
      relateUnrelateEntities.success({
        associationsState,
        deal,
        person,
        unrelate: unrelate ?? false,
      })
    );

    if (deal && updateDeal) {
      yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
    }
    if (person && updatePerson) {
      yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [person] }));
    }
    if (associatedCompany && updateCompany) {
      yield put(
        notifyAboutChanges({ entityType: EntityType.COMPANY, updated: [associatedCompany] })
      );
    }
  } catch (error) {
    yield put(relateUnrelateEntities.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchThumbnail({ payload }: ReturnType<typeof fetchThumbnail>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const activity: Activity | undefined = yield select(getActivity);
    const fileData: Blob = yield callApi(
      "fetchFile",
      orgId,
      payload.fileId,
      false,
      true,
      EntityType.ACTIVITY,
      activity!.id,
      { responseType: "blob" }
    );
    yield call(payload.callback, fileData);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onUploadActivityFiles({ payload }: ReturnType<typeof uploadActivityFiles.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const activity: Activity | undefined = yield select(getActivity);
    const fileGroupId = payload.fileGroupId ?? v4();
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", orgId, file, false, EntityType.ACTIVITY, activity!.id, {
          headers: { "x-mmc-file-group-id": fileGroupId },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadActivityFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadActivityFiles.failure());
    yield put(handleError({ error }));
  }
}

function* onDeleteActivityFile({ payload }: ReturnType<typeof deleteActivityFile.request>) {
  const activity: Activity | undefined = yield select(getActivity);
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const notificationKey = `file_removal_${payload.id}`;
    const cancelled = new Promise<true>((resolve) => {
      notification.info({
        duration: NOTIFICATION_DURATION_WITH_ACTION,
        key: notificationKey,
        message: getFileRemovedNotificationNode(i18nService.getIntl(), payload, () => {
          resolve(true);
          notification.close(notificationKey);
        }),
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let markWindowClosed = (flag: true) => {};
    const windowClosed = new Promise<boolean>((resolve) => {
      markWindowClosed = resolve;
    });
    const documentListener = () => {
      if (document.visibilityState === "hidden") {
        markWindowClosed(true);
        notification.close(notificationKey);
      }
    };
    document.addEventListener("visibilitychange", documentListener);
    const windowListener = () => markWindowClosed(true);
    window.addEventListener("beforeunload", windowListener);

    const result: { cancelled?: Promise<true>; delete?: true; windowClosed?: Promise<true> } =
      yield race({
        cancelled,
        delete: delay(NOTIFICATION_DURATION_WITH_ACTION * 1000),
        windowClosed,
      });

    window.removeEventListener("beforeunload", windowListener);
    document.removeEventListener("visibilitychange", documentListener);

    if (result.delete || result.windowClosed) {
      yield callApi("deleteEntityFile", orgId, EntityType.ACTIVITY, activity!.id, payload.id);
      yield put(
        deleteActivityFile.success({ activityId: activity!.id, file: payload, removed: true })
      );
    } else {
      yield put(
        deleteActivityFile.success({ activityId: activity!.id, file: payload, removed: false })
      );
    }
  } catch (error) {
    yield put(deleteActivityFile.failure({ activityId: activity?.id!, file: payload }));
    yield put(handleError({ error }));
  }
}

function* onDownloadActivityFile({ payload: file }: ReturnType<typeof downloadActivityFile>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const activity: Activity | undefined = yield select(getActivity);

    const key = `download_file_${file.id}`;
    const timeout = setTimeout(() => {
      notification.info({
        duration: 0,
        key,
        message: getYourDownloadWillStartShortlyNode(i18nService.getIntl(), file.name),
      });
    }, 2000);

    const fileBlob: Blob = yield callApi(
      "fetchFile",
      orgId,
      file.id,
      true,
      false,
      EntityType.ACTIVITY,
      activity!.id,
      { responseType: "blob", timeout: 0 }
    );
    clearTimeout(timeout);
    notification.close(key);

    yield call(downloadFileByUrl, window.URL.createObjectURL(fileBlob), file.name);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onBulkUpdateFiles({
  payload: { callback, files },
}: ReturnType<typeof bulkUpdateFiles.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const recordData: ActivityRecordData = yield select(getRecordData);

    if (!recordData.activity) {
      return;
    }

    yield callApi("bulkUpdateFile", orgId, files);

    const updatedFilesResponse: ListResponse<RawFile> = yield callApi(
      "fetchEntityFiles",
      orgId,
      EntityType.ACTIVITY,
      recordData.activity.id,
      {
        $limit: 1000,
        $order: "-createdAt",
      }
    );

    if (callback) {
      yield call(callback);
    }

    yield put(bulkUpdateFiles.success(updatedFilesResponse.data));
  } catch (error) {
    yield put(bulkUpdateFiles.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteActivity({ payload }: ReturnType<typeof deleteActivity.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const activity: Activity | undefined = yield select(getActivity);

    if (!activity) {
      return;
    }

    yield callApi("deleteActivity", orgId, activity.id, payload?.deleteType);
    if (payload?.callback) {
      yield call(payload.callback);
    }
    yield put(deleteActivity.success());

    notification.success({
      description: i18nService.formatMessage(
        defineMessage({
          id: "activityRecordPane.deleteActivity.success.description",
          defaultMessage: "To restore this record, go to Data in Settings.",
          description: "Description of a success notification when an activity is deleted",
        }),
        "To restore this record, go to Data in Settings."
      ),
      message: i18nService.formatMessage(
        defineMessage({
          id: "activityRecordPane.deleteActivity.success.message",
          defaultMessage: "Activity successfully deleted",
          description: "Message of a success notification when an activity is deleted",
        }),
        "Activity successfully deleted"
      ),
    });
    yield put(notifyAboutChanges({ deletedIds: [activity.id], entityType: EntityType.ACTIVITY }));
  } catch (error) {
    yield put(deleteActivity.failure());
    yield put(handleError({ error }));
  }
}

const CHILD_ACTIVITIES_PAGE_SIZE = 10;

function* onFetchChildActivities({ payload }: ReturnType<typeof fetchChildActivities.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const activity: Activity | undefined = yield select(getActivity);

    if (!activity) {
      yield put(fetchChildActivities.failure());
      return;
    }

    // Bulk emails list every recipient, so load all children at once.
    const isBulkEmailParent = isBulkEmailParentActivity(activity);

    const { childActivities: currentChildren }: ActivityRecordData = yield select(getRecordData);
    const offset = !isBulkEmailParent && payload.loadMore ? currentChildren.length : 0;

    const showSharedActivities = activitySettings.getSharedActivitiesVisibility();

    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        $and: [{ parentId: { $eq: activity.id } }],
        includeEmails: true,
        includeFiles: true,
        ...(showSharedActivities
          ? {
              includeAccountSharedActivities: true,
              includeContactSharedActivities: true,
              includeDealSharedActivities: true,
            }
          : {}),
      },
      $limit: isBulkEmailParent ? -1 : CHILD_ACTIVITIES_PAGE_SIZE,
      $offset: offset,
      $order: "-createdAt",
    });

    yield put(
      fetchChildActivities.success({
        activities: response.data,
        loadMore: offset > 0,
        total: response.total ?? response.data.length,
      })
    );
  } catch (error) {
    yield put(fetchChildActivities.failure());
    yield put(handleError({ error }));
  }
}

function* onShowEntityView({ payload }: ReturnType<typeof showEntityView>) {
  const entityTypesWithExternalPreview: EntityType[] = yield select(
    getEntityTypesWithExternalPreview
  );

  if (payload.entityType && !entityTypesWithExternalPreview.includes(payload.entityType)) {
    return;
  }

  const { pathname }: RouterLocation<unknown> = yield select(getLocation);
  const isOnActivityPage = pathname.startsWith(Path.ACTIVITY);

  if (isOnActivityPage) {
    const source: EntityViewSource = yield select(getEntityViewSource);

    if (source === EntityViewSource.GLOBAL_SEARCH) {
      yield put(push(`${pathname}/${payload.entityId}`));
    }
  }
}

export function* activityRecordSaga() {
  yield takeLatest(addActivityCompany.request, onAddActivityCompany);
  yield takeLatest(addActivityDeal.request, onAddActivityDeal);
  yield takeLatest(bulkUpdateFiles.request, onBulkUpdateFiles);
  yield takeLatest(changeAssociatedEntities.request, onChangeAssociatedEntities);
  yield takeLatest(deleteActivity.request, onDeleteActivity);
  yield takeLatest(deleteActivityFile.request, onDeleteActivityFile);
  yield takeLatest(fetchChildActivities.request, onFetchChildActivities);
  yield takeEvery(downloadActivityFile, onDownloadActivityFile);
  yield takeEvery(fetchThumbnail, onFetchThumbnail);
  yield takeLatest(fetchPreviewData.request, onFetchPreviewData);
  yield takeLatest(postponeActivity.request, onPostponeActivity);
  yield takeEvery(relateUnrelateEntities.request, onRelateUnrelateEntities);
  yield takeLatest(removeActivityCompany.request, onRemoveActivityCompany);
  yield takeLatest(removeActivityDeal.request, onRemoveActivityDeal);
  yield takeLatest(setActivityPeople.request, onSetActivityPeople);
  yield takeLatest(toggleCompleteActivity.request, onToggleCompleteActivity);
  yield takeLatest(updateActivity.request, onUpdateActivity);
  yield takeLatest(showEntityView, onShowEntityView);
  yield takeLatest(uploadActivityFiles.request, onUploadActivityFiles);
}
